import { useState } from 'react';
import axios from 'axios';
import { Database, Loader2, Trash2, X } from 'lucide-react';
import { modalBackdropCls, modalCardCls, modalCloseCls, overlayCls } from '../overlay';
import { directionLabel } from '../viewDirections';
import { StepArrows, useStepThrough } from '../stepThrough';
import { useLanguage } from '../../../contexts/LanguageContext';
import { IS_DEMO } from '../../../apiBase';

// One catalog picture — a schematic OR a real photo — zoomed, with (for photos) the
// model that drew it named underneath and a button to remove just this one. Both
// columns use it; the caller passes the image source, the delete endpoint and the
// delete label so the modal is the same for a drawing and a photograph.
export function CatalogImageModal({
  src,
  modelLabel,
  runSeq,
  runFromDev,
  runMode,
  view,
  context,
  deleteUrl,
  deleteLabel,
  asDbToggle,
  id,
  siblings,
  onNavigate,
  onDeleted,
  onClose,
}: {
  src: string;
  // The image-generation model that produced this picture (photos only); omitted for
  // schematics and for photos with no recorded model.
  modelLabel?: string | null;
  // The run (seq number) of the source manual this photo was generated in; omitted for
  // schematics and photos that predate run tracking.
  runSeq?: number | null;
  // Whether that run was a DEV run (true) or a PROD run (false); null/omitted when unknown.
  runFromDev?: boolean | null;
  // Which pipeline the run belonged to — 'llm' (video) or 'models' (3D). Omitted for a
  // photo made before this was recorded, and for one added by hand.
  runMode?: string | null;
  // The angle the picture was ASKED for. Empty when none was — every LLM-pipeline photo,
  // since that pipeline has no angle to ask for.
  view?: string | null;
  // Show removal as the "Add to database" toggle (ON, since a catalog photo IS in the DB)
  // instead of a plain delete button — the same control as Phase 2, so the two stay in sync.
  // Turning it off removes the photo (the same delete). Photos only.
  asDbToggle?: boolean;
  // The images the generator was given as context (the part's schematic + the notice's
  // product photos), shown small in a row under the picture. Omitted for schematics and
  // for the product-photos table, where there is no generation context.
  context?: string[];
  // The DELETE endpoint for this one picture (schematic or image).
  deleteUrl: string;
  deleteLabel: string;
  // Which picture this is, and the set it belongs to (the strip it was opened from, as
  // ids in the order that strip shows them), so ← and → walk from one to the next without
  // closing and reopening — the only way to compare two pictures, since you cannot hold
  // them both in your head at thumbnail size. Omit all three for a lone picture: no
  // arrows, and the keys stay whoever's they were.
  id?: number;
  siblings?: number[];
  onNavigate?: (id: number) => void;
  onDeleted: () => void;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const [deleting, setDeleting] = useState(false);
  const hasContext = !!context?.length;
  const { canStep, step } = useStepThrough(siblings, id, onNavigate);

  const del = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await axios.delete(deleteUrl);
      onDeleted();
    } catch {
      setDeleting(false);
    }
  };

  return (
    <div className={`${overlayCls} ${modalBackdropCls}`} onClick={onClose}>
      <div
        className={`relative flex max-h-[90vh] w-full max-w-2xl flex-col items-center gap-4 pb-5 pt-12 ${canStep ? 'px-16' : 'px-5'} ${modalCardCls}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('notice.catalog.cancel')}
          className={modalCloseCls}
        >
          <X className="h-5 w-5" />
        </button>
        {canStep && <StepArrows onStep={step} />}

        {/* A little smaller when a context row rides underneath, to save room. */}
        <img
          src={src}
          alt=""
          className={`${hasContext ? 'max-h-[46vh]' : 'max-h-[60vh]'} w-auto rounded-lg bg-white`}
        />

        {/* The images fed to the generator as context (schematic + product photos), in a
            single scrollable row under the picture. */}
        {hasContext && (
          <div className="flex w-full flex-col items-center gap-1.5">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
              {t('notice.catalog.contextTitle')}
            </div>
            <div className="flex max-w-full gap-2 overflow-x-auto py-0.5">
              {context!.map((u) => (
                <img
                  key={u}
                  src={u}
                  alt=""
                  className="h-16 w-16 shrink-0 rounded-md border border-slate-200 bg-white object-contain"
                />
              ))}
            </div>
          </div>
        )}

        {/* The run of the source manual this photo came from (photos with a recorded run),
            with a PROD/DEV badge when the run's origin is known. */}
        {typeof runSeq === 'number' && (
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            {typeof runFromDev === 'boolean' && (
              <span
                className={
                  'rounded px-1 py-px text-[10px] font-semibold uppercase leading-none tracking-wide ' +
                  (runFromDev
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-blue-100 text-blue-700')
                }
              >
                {runFromDev ? 'DEV' : 'PROD'}
              </span>
            )}
            {t('notice.cat.run')} {runSeq}
            {/* Which pipeline, beside the run number it qualifies: the two number their
                runs independently, so "Run 9" alone names two different runs. */}
            {runMode && (
              <span className="rounded px-1 py-px text-[10px] font-semibold uppercase leading-none tracking-wide bg-slate-200 text-slate-600">
                {t(runMode === 'models' ? 'notice.mode.models' : IS_DEMO ? 'notice.mode.llmDemo' : 'notice.mode.llm')}
              </span>
            )}
          </div>
        )}

        {/* The angle it was asked for. Nothing when none was — a photo from the pipeline
            that does not ask for one has no angle to report, which is different from an
            angle nobody recorded. */}
        {view && (
          <div className="flex items-center gap-1.5 text-sm text-slate-600">
            <span className="text-slate-400">{t('notice.3d.angle')}</span>
            <span className="font-semibold text-blue-700">
              {directionLabel(view)}
            </span>
          </div>
        )}

        {/* The model that generated it (photos only). */}
        {modelLabel && (
          <div className="flex items-center gap-1.5 text-sm text-slate-600">
            <span className="text-slate-400">{t('notice.catalog.photoModel')}</span>
            <span className="font-medium">{modelLabel}</span>
          </div>
        )}

        {asDbToggle ? (
          // ON (this photo is in the DB); toggling it off removes it — same control as Phase 2.
          <button
            type="button"
            onClick={del}
            disabled={deleting}
            role="switch"
            aria-checked
            title={t('notice.step3.addedToDb')}
            className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-50"
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
            {t('notice.step3.addedToDb')}
          </button>
        ) : (
          <button
            type="button"
            onClick={del}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-500 transition-colors hover:border-rose-400 hover:text-rose-600 disabled:opacity-50"
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {deleteLabel}
          </button>
        )}
      </div>
    </div>
  );
}
